import React, { createContext, useContext, useMemo } from 'react';
import LineItemDetails, {
  LineItemFulfillmentHold,
  LineItemGroupInfo,
  LineItemState,
  LineItemTrackingData,
  ProductAttribute,
} from '../../types/lineItemDetails';
import MonetaryAmount from '../../types/MonetaryAmount';
import CorePricedItem from '../../types/lineItemDetails/CorePricedItem';
import { PricingData } from '../../types/pricingData';

interface ILineItemDataContext {
  orderNumber: string;
  item: LineItemDetails;
}

const LineItemDataContext = createContext<ILineItemDataContext | null>(null);

type LineItemDataProviderProps = {
  children: React.ReactNode;
  item: LineItemDetails;
  orderNumber: string;
};

/**
 * A context provider to make line item data available to all children components.
 *
 * LineItem data access should be requested via the `useLineItemData` for consistency and default null values.
 */
const LineItemDataProvider = ({
  children,
  item,
  orderNumber,
}: LineItemDataProviderProps) => {
  const lineItemDataContextValues = useMemo(
    () => ({
      orderNumber,
      item,
    }),
    [item, orderNumber]
  );

  return (
    <LineItemDataContext.Provider value={lineItemDataContextValues}>
      {children}
    </LineItemDataContext.Provider>
  );
};

type PartialTracking = {
  trackingUrl: string | null;
};

type NormalizedTracking = LineItemTrackingData[] | PartialTracking[] | null;

type LineItemData = {
  id: string | null;
  orderNumber: string | null;
  productName: string | null;
  productKey: string | null;
  quantity: number | null;
  states: LineItemState[];
  pricing: {
    totalDiscountedPrice: MonetaryAmount | null;
    totalListPrice: MonetaryAmount | null;
  };
  documentUrl: string | null;
  livePreviewUrl: string | null;
  editDocumentUrl: string | null;
  legacyProductConfigId: string | null;
  actions: {
    reorder: boolean;
  };
  pricingBreakdown: CorePricedItem | null;
  docReviewRequired: boolean | null;
  docReviewNotes: string | null;
  customOrderData: object | null;
  purcsData: object | null;
  cmrd: string | null;
  trackingData: NormalizedTracking;
  uxModSpecs: object[];
  attributes: ProductAttribute[];
  productVersion: number | null;
  productConfigUrl: string | null;
  fulfillmentHoldsV2: LineItemFulfillmentHold[];
  groupInfo: LineItemGroupInfo | Record<string, any>;
  isGroupItem: boolean;
  deliverability: {
    itemDeliverable: boolean;
  };
  pricingData: PricingData | object;
  shippingPricingData: PricingData | object;
};

/**
 *
 * @returns A standard line item data representation with `null` fallbacks for missing data.
 */
const useLineItemData = (): LineItemData => {
  const context = useContext(LineItemDataContext);
  const item = context?.item;
  const trackingUrl = item?.trackingUrl || null;
  const trackingData = item?.trackingData || null;
  const isGroupItem = !!item?.groupInfo?.groupId;

  const trackingNormalize: NormalizedTracking =
    trackingUrl && !trackingData ? [{ trackingUrl }] : trackingData;

  const returnItem: LineItemData = {
    id: item?.id || null,
    orderNumber: context?.orderNumber || null,
    productName: item?.productName || null,
    productKey: item?.productKey || null,
    quantity: item?.quantity || null,
    states: item?.states || [],
    pricing: {
      totalDiscountedPrice: item?.totalDiscountedPrice || null,
      totalListPrice: item?.totalListPrice || null,
    },
    documentUrl: item?.documentUrl || null,
    livePreviewUrl: item?.livePreviewUrl || null,
    editDocumentUrl: item?.editDocumentUrl || null,
    legacyProductConfigId: item?.legacyProductConfigId || null,
    actions: {
      reorder: item?.isReorderable || false,
    },
    pricingBreakdown: item?.pricingBreakdown || null,
    docReviewRequired: item?.docReviewRequired || null,
    docReviewNotes: item?.docReviewNotes || null,
    customOrderData: item?.customOrderData || null,
    purcsData: item?.purcsData || null,
    cmrd: item?.promisedArrivalDate || null,
    trackingData: trackingNormalize,
    uxModSpecs: item?.uxModSpecs || [],
    attributes: item?.displayProductAttributes || [],
    productVersion: item?.productVersion || null,
    productConfigUrl: item?.productConfigUrl || null,
    fulfillmentHoldsV2: item?.fulfillmentHoldsV2 || [],
    groupInfo: item?.groupInfo || {},
    isGroupItem,
    deliverability: {
      itemDeliverable: item?.isDeliverable || false,
    },
    pricingData: item?.pricingData || {},
    shippingPricingData: item?.shippingPricingData || {},
  };

  return returnItem;
};

export { LineItemDataProvider, useLineItemData };
